import React, { useEffect, useMemo, useState } from "react";
import useApiStable from "../../utils/useApiStable";
import { useAuth } from "../../context/AuthContext";
import { canManageHeating } from "../../utils/userUtils";
import "../../styles/HeatingPlan.css";

const SLOT_MINUTES = 30;
const SLOTS_PER_DAY = 24 * 60 / SLOT_MINUTES;

const DAYS = [
  { key: "MONDAY", label: "Mo" },
  { key: "TUESDAY", label: "Di" },
  { key: "WEDNESDAY", label: "Mi" },
  { key: "THURSDAY", label: "Do" },
  { key: "FRIDAY", label: "Fr" },
  { key: "SATURDAY", label: "Sa" },
  { key: "SUNDAY", label: "So" },
];

const emptySlots = () => Array.from({ length: SLOTS_PER_DAY }, () => false);

const HeatingPlan = () => {
  const api = useApiStable();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [comfortTemp, setComfortTemp] = useState(22);
  const [setbackTemp, setSetbackTemp] = useState(16);
  const [daySlots, setDaySlots] = useState(() => {
    const init = {};
    DAYS.forEach((d) => { init[d.key] = emptySlots(); });
    return init;
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canAccessHeating = useMemo(() => canManageHeating(user), [user]);

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const data = await api.get("/heating/groups");
        const names = Array.isArray(data)
          ? data.map((g) => g.name)
          : Object.values(data || {}).map((g) => g.name || g);
        setGroups(names);
        if (!selectedRoom && names.length > 0) {
          setSelectedRoom(names[0]);
        }
      } catch (err) {
        setError(err.message || "Heizgruppen konnten nicht geladen werden");
      }
    };
    loadGroups();
  }, [api]);

  useEffect(() => {
    if (!selectedRoom) return;
    const loadPlan = async () => {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        const plan = await api.get(`/heating/plan?room=${encodeURIComponent(selectedRoom)}`);
        setComfortTemp(plan?.comfortTemp ?? 22);
        setSetbackTemp(plan?.setbackTemp ?? 16);
        const nextSlots = {};
        DAYS.forEach((d) => { nextSlots[d.key] = emptySlots(); });
        (plan?.switches || []).forEach((sw) => {
          const arr = nextSlots[sw.day];
          if (!arr) return;
          const startIndex = Math.min(SLOTS_PER_DAY - 1, Math.max(0, Math.floor(sw.hour * 2 + (sw.minute >= 30 ? 1 : 0))));
          const durationSlots = Math.max(1, Math.ceil(sw.duration / SLOT_MINUTES));
          const until = Math.min(SLOTS_PER_DAY, startIndex + durationSlots);
          const isOn = sw.targetValue >= (plan?.comfortTemp ?? 22);
          for (let i = startIndex; i < until; i++) {
            arr[i] = isOn;
          }
        });
        setDaySlots(nextSlots);
      } catch (err) {
        setError(err.message || "Plan konnte nicht geladen werden");
      } finally {
        setLoading(false);
      }
    };
    loadPlan();
  }, [api, selectedRoom]);

  const toggleSlot = (dayKey, index) => {
    setDaySlots((prev) => {
      const copy = { ...prev, [dayKey]: [...prev[dayKey]] };
      copy[dayKey][index] = !copy[dayKey][index];
      return copy;
    });
  };

  const clearDay = (dayKey) => {
    setDaySlots((prev) => ({ ...prev, [dayKey]: emptySlots() }));
  };

  const slotsToSwitches = () => {
    const switches = [];
    DAYS.forEach((day) => {
      const slots = daySlots[day.key] || [];
      let idx = 0;
      while (idx < slots.length) {
        if (!slots[idx]) {
          idx++;
          continue;
        }
        const start = idx;
        while (idx < slots.length && slots[idx]) {
          idx++;
        }
        const length = idx - start;
        const hour = Math.floor(start / 2);
        const minute = start % 2 === 0 ? 0 : 30;
        const duration = length * SLOT_MINUTES;
        switches.push({
          day: day.key,
          hour,
          minute,
          duration,
          targetValue: comfortTemp,
        });
      }
    });
    return switches;
  };

  const savePlan = async () => {
    if (!selectedRoom) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        roomName: selectedRoom,
        comfortTemp: Number(comfortTemp),
        setbackTemp: Number(setbackTemp),
        switches: slotsToSwitches(),
      };
      await api.put("/heating/plan", payload);
      setSuccess("Plan gespeichert");
    } catch (err) {
      setError(err.message || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const renderScale = () => (
    <div className="hp-scale-row">
      <div className="hp-scale-label" aria-hidden="true" />
      <div className="hp-scale">
        {Array.from({ length: 24 }).map((_, h) => (
          <div key={h} className="hp-scale-hour" style={{ gridColumn: `${h * 2 + 1} / span 2` }}>
            {h}:00
          </div>
        ))}
      </div>
      <div className="hp-actions" aria-hidden="true" />
    </div>
  );

  const renderDayRow = (day) => {
    const slots = daySlots[day.key] || [];
    return (
      <div key={day.key} className="hp-day-row">
        <div className="hp-day-label">{day.label}</div>
        <div className="hp-day-grid">
          {slots.map((isOn, idx) => (
            <button
              key={`${day.key}-${idx}`}
              type="button"
              className={`hp-slot ${isOn ? "on" : "off"}`}
              title={`${day.label} ${Math.floor(idx / 2)}:${idx % 2 === 0 ? "00" : "30"}`}
              onClick={() => toggleSlot(day.key, idx)}
            />
          ))}
        </div>
        <div className="hp-actions">
          <button type="button" className="hp-clear" onClick={() => clearDay(day.key)}>Tag leeren</button>
        </div>
      </div>
    );
  };

  if (!canAccessHeating) {
    return <div className="hp-page">Nur Admins oder Mitglieder der Gruppe HEATING dürfen Heizpläne bearbeiten.</div>;
  }

  return (
    <div className="hp-page">
      <div className="hp-header">
        <h2>Heizplan</h2>
        <div className="hp-room-select">
          <label>
            Gruppe
            <select value={selectedRoom} onChange={(e) => setSelectedRoom(e.target.value)}>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
        </div>
        <div className="hp-temps">
          <label>
            Comfort
            <input type="number" step="0.5" value={comfortTemp} onChange={(e) => setComfortTemp(e.target.value)} />
          </label>
          <label>
            Absenk
            <input type="number" step="0.5" value={setbackTemp} onChange={(e) => setSetbackTemp(e.target.value)} />
          </label>
        </div>
        <button className="hp-save" onClick={savePlan} disabled={saving || loading || !selectedRoom}>
          {saving ? "Speichern..." : "Speichern"}
        </button>
      </div>

      {error && <div className="hp-error">{error}</div>}
      {success && <div className="hp-success">{success}</div>}

      <div className="hp-grid-container">
        {renderScale()}
        {DAYS.map(renderDayRow)}
      </div>
    </div>
  );
};

export default HeatingPlan;
